if (!customElements.get('feefo-reviews-slider')) {
  class FeefoReviewsSlider extends HTMLElement {
    connectedCallback() {
      this.merchant = this.dataset.merchant;
      this.mode = this.dataset.mode === 'product' ? 'product' : 'service';
      this.sku = this.dataset.sku || '';
      this.count = parseInt(this.dataset.count) || 12;
      this.items = parseInt(this.dataset.items) || 3;
      this.tabletItems = parseInt(this.dataset.tabletItems) || 2;
      this.minRating = parseInt(this.dataset.minRating) || 0;

      this.wrapper = this.querySelector('.feefo-reviews__items');
      this.sliderInstance = false;

      if (!this.merchant || !this.wrapper) return this.hideSection();

      // A product with no SKU can never be matched to a review.
      if (this.mode === 'product' && !this.sku) return this.hideSection();

      this.load();
    }

    // Reviews are fetched at render time, so a product with none hides the
    // whole section — heading, controls and logo included — rather than
    // leaving empty chrome behind. It reappears on its own once Feefo has a
    // review for that SKU, with no change needed here.
    hideSection() {
      const section = this.closest('.feefo-reviews');
      if (section) section.remove();
      else this.remove();
    }

    buildEndpoint() {
      const merchant = encodeURIComponent(this.merchant);
      if (this.mode === 'product') {
        // Product reviews live under review.products[], filtered by variant SKU.
        let url =
          'https://api.feefo.com/api/10/reviews/product?merchant_identifier=' +
          merchant +
          '&page_size=' +
          this.count;
        if (this.sku) url += '&product_sku=' + encodeURIComponent(this.sku);
        return url;
      }
      return (
        'https://api.feefo.com/api/10/reviews/all?merchant_identifier=' +
        merchant +
        '&page_size=' +
        this.count +
        '&sort=-created_at'
      );
    }

    extractReviews(data) {
      const items = data.reviews || [];
      if (this.mode === 'product') {
        return items.reduce((acc, item) => acc.concat(item.products || []), []);
      }
      return items.map((item) => item.service);
    }

    async load() {
      const endpoint = this.buildEndpoint();

      try {
        const response = await fetch(endpoint);
        if (!response.ok) throw new Error('Feefo request failed: ' + response.status);
        const data = await response.json();
        const reviews = this.extractReviews(data)
          .filter((review) => review && review.review && review.review.trim() !== '')
          .filter((review) => !this.minRating || (review.rating && review.rating.rating >= this.minRating));

        if (!reviews.length) {
          this.hideSection();
          return;
        }

        // A single review can't be a slider — one slide stretched across a
        // three-column track sits off to one side, and looping would just clone
        // it. Anything more stays a slider, which still swipes one-up on mobile.
        this.isStatic = reviews.length === 1;

        this.render(reviews);

        if (this.isStatic) this.setStaticLayout();
        else this.initSlider(reviews.length);
      } catch (error) {
        console.warn('[feefo-reviews]', error);
        this.hideSection();
      }
    }

    render(reviews) {
      const fragment = document.createDocumentFragment();

      reviews.forEach((review) => {
        const column = document.createElement('div');
        column.className = this.isStatic ? 'feefo-review' : 'f-column swiper-slide feefo-review';

        const card = document.createElement('div');
        card.className = 'feefo-review__card';

        const rating = review.rating && review.rating.rating ? Math.round(review.rating.rating) : 0;
        if (rating) {
          const stars = document.createElement('div');
          stars.className = 'feefo-review__stars';
          stars.setAttribute('role', 'img');
          stars.setAttribute('aria-label', rating + ' out of 5 stars');
          stars.textContent = '★★★★★'.slice(0, rating) + '☆☆☆☆☆'.slice(0, 5 - rating);
          card.appendChild(stars);
        }

        if (review.title) {
          const title = document.createElement('p');
          title.className = 'feefo-review__title';
          title.textContent = review.title;
          card.appendChild(title);
        }

        const text = document.createElement('blockquote');
        text.className = 'feefo-review__text';
        text.textContent = review.review;
        card.appendChild(text);

        if (review.created_at) {
          const date = document.createElement('p');
          date.className = 'feefo-review__date';
          const parsed = new Date(review.created_at);
          if (!isNaN(parsed)) {
            date.textContent = parsed.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
            card.appendChild(date);
          }
        }

        column.appendChild(card);
        fragment.appendChild(column);
      });

      this.wrapper.innerHTML = '';
      this.wrapper.appendChild(fragment);
      this.removeAttribute('data-loading');
    }

    setStaticLayout() {
      const section = this.closest('.feefo-reviews');
      const controls = section && section.querySelector('.feefo-reviews__controls');
      if (controls) controls.remove();

      this.wrapper.className = 'feefo-reviews__items feefo-reviews__items--static';
    }

    initSlider(count) {
      if (!window.FoxTheme || !window.FoxTheme.Carousel) return;

      const section = this.closest('.feefo-reviews');
      this.markControlsRedundancy(section, count);

      // Never ask for more columns than there are reviews, or the track is laid
      // out for three and two cards sit squashed against the left edge.
      const desktopItems = Math.min(this.items, count);
      const tabletItems = Math.min(this.tabletItems, count);

      const options = {
        slidesPerView: 1,
        spaceBetween: 24,
        navigation: {
          nextEl: section.querySelector('.swiper-btn-next'),
          prevEl: section.querySelector('.swiper-btn-prev'),
        },
        pagination: {
          el: section.querySelector('.swiper-pagination'),
          clickable: true,
        },
        breakpoints: {
          768: { slidesPerView: tabletItems, spaceBetween: 24 },
          1024: { slidesPerView: desktopItems, spaceBetween: 32 },
        },
        // Looping clones slides to fill the track, so only loop when there are
        // genuinely more reviews than the widest row shows.
        loop: count > this.items,
        threshold: 2,
      };

      this.classList.add('swiper');
      this.wrapper.classList.remove('f-grid');
      this.wrapper.classList.add('swiper-wrapper');
      this.sliderInstance = new window.FoxTheme.Carousel(this, options);
      this.sliderInstance.init();

      // Slides are injected after page load, so force a recalculation once
      // layout has settled — otherwise Swiper measures a zero-width container.
      requestAnimationFrame(() => {
        if (this.sliderInstance && this.sliderInstance.slider) {
          this.sliderInstance.slider.update();
        }
      });
    }

    // At wider breakpoints every review can be on screen at once, leaving the
    // arrows and bullets with nothing to do — but they're still needed on
    // mobile, where the cards slide one at a time.
    //
    // This is a class plus a CSS media query rather than a JS width check on
    // purpose: the carousel is built asynchronously, so anything toggled from
    // here on the first frame gets undone once it finishes initialising.
    markControlsRedundancy(section, count) {
      if (count <= this.tabletItems) section.classList.add('feefo-reviews--fits-tablet');
      else if (count <= this.items) section.classList.add('feefo-reviews--fits-desktop');
    }
  }

  customElements.define('feefo-reviews-slider', FeefoReviewsSlider);
}
